import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';

import { ResponsiveTag } from './responsive-tag';
import { useOnElementWidthChange } from '../../hooks/use-on-element-width-change';
import { TagPropType } from '../../prop-types/tag';

import style from './responsive-tag-list.module.less';
import { useSalesOrderIdContext } from '../../orders/context/sales-order-id-context-provider';
import { useEntitiesSalesOrderProperty } from '../../hooks/entities/selectors/sales-orders';
import { MarketplaceIds } from '../../enums/marketplace-ids';

export const ResponsiveTagList = ({ tags, className, tagClassName }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [tagWidthMap, setTagWidthMap] = useState({});
  const containerRef = useRef(null);
  const store = useEntitiesSalesOrderProperty(useSalesOrderIdContext(), 'store');

  const addTagWidth = useCallback((tagId, width) => {
    setTagWidthMap((prevState) => {
      return {
        ...prevState,
        [tagId]: width,
      };
    });
  }, []);

  const removeTagWidth = useCallback((tagId) => {
    setTagWidthMap((prevState) => {
      const newState = { ...prevState };
      delete newState[tagId];
      return newState;
    });
  }, []);

  const checkIfTagsShouldCollapse = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const containerStyle = getComputedStyle(containerRef.current);
    const containerGap = parseInt(containerStyle.gap, 10) || 0;

    const expandedTagsWidth = Object.values(tagWidthMap).reduce((acc, tagWidth, tagIndex) => {
      if (tagIndex === 0) {
        return acc + tagWidth;
      }
      return acc + tagWidth + containerGap;
    }, 0);

    const shouldCollapseTags = expandedTagsWidth > containerWidth;

    setIsCollapsed(shouldCollapseTags);
  }, [tagWidthMap]);

  useLayoutEffect(checkIfTagsShouldCollapse, [checkIfTagsShouldCollapse]);

  useOnElementWidthChange(containerRef, checkIfTagsShouldCollapse);

  const isShopifyPromise = (name) => store?.marketplaceId === MarketplaceIds.Shopify.toString() && name === 'promise';

  return (
    <div ref={containerRef} className={classnames(style['responsive-tag-list'], { [style['is-collapsed']]: isCollapsed }, className)}>
      {tags
        .slice()
        .sort((a, b) => {
          if (isShopifyPromise(a.name)) return -1;
          if (isShopifyPromise(b.name)) return 1;
          return 0;
        })
        .map(({ name, tagId, hexColor }, index) => {
          return (
            <ResponsiveTag
              key={tagId}
              hexColor={hexColor}
              index={index}
              isShopifyPromise={isShopifyPromise(name)}
              name={name}
              onTagMount={addTagWidth}
              onTagUnmount={removeTagWidth}
              shouldCollapse={isCollapsed}
              tagClassName={tagClassName}
              tagId={tagId}
            />
          );
        })}
    </div>
  );
};

ResponsiveTagList.propTypes = {
  tags: PropTypes.arrayOf(TagPropType).isRequired,
  className: PropTypes.string,
  tagClassName: PropTypes.string,
};
